import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/styles';
import { appInsights } from '../../../auth/appInsight';
import PropTypes from 'prop-types';
import { getDynamicBorder, getStringProduct } from '../../../utils/utilityFunctions';

const FreedomButton = styled(Button)(
  ({
    theme,
    btncolor,
    btnborder,
    width,
    padding,
    lineheight,
    borderradius,
    btnbackground,
    gap,
    fontsize,
    xlfontsize,
    xllineheight,
    height,
    xlheight,
    fontweight,
    btnboxshadow,
    btnbackground1,
    btnboxshadow1,
    btnhovercolor,
    btndisabledcolor,
    btndisabledborder,
    btndisabledbg,
    texthovercolor,
    disabled,
    colorbtn,
    isAnimation // New prop for animation
  }) => ({
    background: disabled ? 'transparent' : btnbackground || 'transparent',
    boxShadow: `0px 4px 14px ${btnboxshadow}`,
    border: btnborder ? `2px solid ${btnborder}` : `2px solid ${btncolor}`,
    borderRadius: borderradius || '10px',
    gap: gap,
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: fontweight || '500',
    fontSize: fontsize || '16px',
    lineHeight: lineheight || '15px',
    color: colorbtn || btncolor || theme.palette.primary.white,
    textTransform: 'capitalize',
    width: width || '100%',
    height: height || '56px',
    padding: padding || '2px 6px',
    animation: isAnimation && !disabled ? 'pulse 1.5s ease-in-out infinite' : 'none',
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        boxShadow: `0 0 0 0 ${theme.palette.primary.main}` // Red glow (adjust color)
      },
      '70%': {
        transform: 'scale(1.05)',
        boxShadow: '0 0 10px 10px rgba(255, 65, 65, 0)' // Expand and fade glow
      },
      '100%': {
        transform: 'scale(1)',
        boxShadow: '0 0 0 0 rgba(255, 65, 65, 0)' // Reset
      }
    },
    '&:hover': {
      background: btnhovercolor || theme.palette.button.buttonbgcolor,
      animation: isAnimation && !disabled ? 'none' : undefined // Stop animation on hover if needed
    },
    '&:focus': {
      border: `2px solid ${theme.palette.text.primary}`
    },
    '&:disabled': {
      color: btndisabledcolor,
      border: btndisabledborder ? `2px solid ${btndisabledborder}` : `2px solid ${btndisabledcolor}`,
      animation: 'none' // Disable animation when button is disabled
    },
    [theme.breakpoints.down('sm')]: {
      padding: '4px',
      fontSize: fontsize || '12px'
    },
    [theme.breakpoints.up('xll')]: {
      width: getStringProduct(1.25, width || '100%'),
      height: getStringProduct(1.25, height || '56px'),
      fontSize: getStringProduct(1.25, fontsize || '16px'),
      lineHeight: getStringProduct(1.25, lineheight || '15px'),
      borderRadius: getStringProduct(1.25, borderradius || '10px'),
      gap: getStringProduct(1.25, gap),
      boxShadow: `${getStringProduct(1.25, '0px 4px 14px')} ${btnboxshadow}`,
      padding: getStringProduct(1.25, padding || '2px 6px'),
      border: getDynamicBorder(1.25, `2px solid ${btnborder || btncolor}`)
    },
    [theme.breakpoints.up('xlll')]: {
      width: getStringProduct(1.66, width || '100%'),
      height: getStringProduct(1.66, height || '56px'),
      fontSize: getStringProduct(1.66, fontsize || '16px'),
      lineHeight: getStringProduct(1.66, lineheight || '15px'),
      borderRadius: getStringProduct(1.66, borderradius || '10px'),
      gap: getStringProduct(1.66, gap),
      boxShadow: `${getStringProduct(1.66, '0px 4px 14px')} ${btnboxshadow}`,
      padding: getStringProduct(1.66, padding || '2px 6px'),
      border: getDynamicBorder(1.66, `2px solid ${btnborder || btncolor}`)
    },
    [theme.breakpoints.up('xl4')]: {
     width: getStringProduct(2.46, width || '100%'),
      height: getStringProduct(2.46, height || '56px'),
      fontSize: getStringProduct(2.46, fontsize || '16px'),
      lineHeight: getStringProduct(2.46, lineheight || '15px'),
      borderRadius: getStringProduct(2.46, borderradius || '10px'),
      gap: getStringProduct(2.46, gap),
      boxShadow: `${getStringProduct(2.46, '0px 4px 14px')} ${btnboxshadow}`,
      padding: getStringProduct(2.46, padding || '2px 6px'),
      border: getDynamicBorder(2.46, `2px solid ${btnborder || btncolor}`)
    }
  })
);

const FreedomButton1 = (props) => {
  const {
    children,
    disabled = false,
    loading = false,
    variant = 'contained',
    sizecircle,
    isAnimation = false, // New prop with default false
    ...rest
  } = props;
  const theme = useTheme();

  try {
    return (
      <React.Fragment>
        {loading ? (
          <FreedomButton
            sx={{ mr: 1, [theme.breakpoints.up('xl4')]: { mr: 2.46 } }}
            disableElevation={true}
            disableRipple={true}
            disableFocusRipple={true}
            variant={variant}
            isAnimation={false} // Disable animation during loading
            {...rest}
            disabled={true}
          >
            <CircularProgress size={sizecircle} sx={{ color: theme.palette.text.primary }} />
          </FreedomButton>
        ) : (
          <FreedomButton
            variant={variant}
            disableElevation={true}
            disableRipple={true}
            disableFocusRipple={true}
            disabled={disabled}
            isAnimation={isAnimation && !disabled} // Only animate if isAnimation is true and not disabled
            {...rest}
          >
            {children}
          </FreedomButton>
        )}
      </React.Fragment>
    );
  } catch (error) {
    appInsights.trackException({ error: new Error(error) });
  }
};

export default FreedomButton1;

FreedomButton1.propTypes = {
  loading: PropTypes.bool,
  isAnimation: PropTypes.bool // Add new prop type
};
